import {useEffect, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {Modal, ModalTitle, Button} from '../common/ui';
import {Icon} from '../common/Icon';
import {
  useRecorder,
  recordingSupported,
  MicDeniedError,
} from '../../hooks/useRecorder';
import {useConfirm} from '../../context/ConfirmProvider';
import {useToast} from '../../context/ToastProvider';
import {formatDuration} from '../../utils';
import {getVoiceLanguage} from '../../services/settings';
import {LANGUAGES, flagEmoji} from '../../services/languages';
import css from './create.module.css';

interface Props {
  open: boolean;
  onClose: () => void;
  onSubmit: (blob: Blob, ext: string, duration: number) => Promise<void>;
}

const BAR_COUNT = 24;

export function RecordModal({open, onClose, onSubmit}: Props) {
  const {t} = useTranslation();
  const confirm = useConfirm();
  const showToast = useToast();
  const recorder = useRecorder();
  const [levels, setLevels] = useState<number[]>(Array(BAR_COUNT).fill(0));
  const [finishing, setFinishing] = useState(false);

  // Rolling waveform history for the level bars.
  useEffect(() => {
    if (recorder.status !== 'recording') {
      return;
    }
    const id = window.setInterval(() => {
      setLevels(current => [...current.slice(1), recorder.level]);
    }, 90);
    return () => window.clearInterval(id);
  }, [recorder.status, recorder.level]);

  // Auto-start when the modal opens.
  useEffect(() => {
    if (!open) {
      return;
    }
    setLevels(Array(BAR_COUNT).fill(0));
    setFinishing(false);
    if (!recordingSupported()) {
      showToast(t('WebRecordingUnsupported'), 'error');
      onClose();
      return;
    }
    recorder.start().catch(e => {
      showToast(
        e instanceof MicDeniedError
          ? t('WebMicDenied')
          : t('Error starting recording'),
        'error',
      );
      onClose();
    });
    return () => recorder.cancel();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const requestClose = async () => {
    if (finishing) {
      return;
    }
    if (recorder.status !== 'idle' && recorder.elapsed > 1) {
      const leave = await confirm({
        title: t('LeaveRecording'),
        message: t('LeaveRecordingText'),
        confirmText: t('Discard'),
        danger: true,
      });
      if (!leave) {
        return;
      }
    }
    recorder.cancel();
    onClose();
  };

  const finish = async () => {
    if (finishing) {
      return;
    }
    setFinishing(true);
    try {
      const result = await recorder.stop();
      if (result.duration < 1) {
        showToast(t('upload_error'), 'error');
        onClose();
        return;
      }
      await onSubmit(result.blob, result.ext, result.duration);
    } catch (e) {
      console.warn(e);
      onClose();
    } finally {
      setFinishing(false);
    }
  };

  const voiceLang = getVoiceLanguage();
  const voiceOption = voiceLang
    ? LANGUAGES.find(lang => lang.code === voiceLang)
    : null;

  return (
    <Modal open={open} onClose={requestClose}>
      <ModalTitle>{t('Start_a_new_note')}</ModalTitle>
      <div className={css.recordBody}>
        <div className={css.micRings}>
          <span
            className={
              recorder.status === 'recording'
                ? css.micRingOuterRecording
                : css.micRingOuter
            }
          />
          <span className={css.micRingInner}>
            <Icon name="mic" size={28} />
          </span>
        </div>
        <div className={css.timer}>{formatDuration(recorder.elapsed)}</div>
        <div className={css.statusText}>
          {recorder.status === 'recording'
            ? t('Recording')
            : recorder.status === 'paused'
              ? t('RecordingPaused')
              : ''}
        </div>
        <div className={css.levelBars} aria-hidden>
          {levels.map((value, i) => (
            <div
              key={i}
              className={css.levelBar}
              style={{
                height: `${5 + value * 46}px`,
                opacity: recorder.status === 'paused' ? 0.4 : 1,
              }}
            />
          ))}
        </div>
        <div className={css.recordControls}>
          <button
            className={css.sideBtn}
            onClick={requestClose}
            title={t('Cancel')}
            type="button"
          >
            <Icon name="x" size={20} />
          </button>
          <button
            className={
              recorder.status === 'recording'
                ? css.recordMainBtnRecording
                : css.recordMainBtn
            }
            onClick={
              recorder.status === 'recording' ? recorder.pause : recorder.resume
            }
            title={recorder.status === 'recording' ? t('Pause') : t('Continue')}
            type="button"
          >
            <Icon
              name={recorder.status === 'recording' ? 'pause' : 'mic'}
              size={26}
            />
          </button>
          <button
            className={css.sideBtn}
            onClick={finish}
            disabled={finishing}
            title={t('Done')}
            type="button"
            style={{
              background: 'var(--color-success)',
              color: '#fff',
              borderColor: 'transparent',
            }}
          >
            <Icon name="check" size={20} />
          </button>
        </div>
        {voiceOption ? (
          <span className={css.langChip}>
            {flagEmoji(voiceOption.flag)} {voiceOption.language}
          </span>
        ) : (
          <span className={css.langChip}>🌐 {t('Automatic')}</span>
        )}
        <Button variant="ghost" onClick={finish} loading={finishing}>
          {t('Done')}
        </Button>
      </div>
    </Modal>
  );
}
